import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import dayjs from 'dayjs';
import { ROOT_URL } from '../../api/ApiConfig';
import {
  applyAnalyticsPendingLoading,
  markAnalyticsFetchKey,
} from '../../utils/analyticsQuery';

/* ----------------------------------------------------------
   ✅ Helper function to detect range type from dates
----------------------------------------------------------- */
const detectRangeType = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const diffDays = end.diff(start, 'day');

  if (start.isSame(end, 'day') && end.isSame(dayjs(), 'day')) return 'today';
  if (start.isSame(end, 'day') && end.isSame(dayjs().subtract(1, 'day'), 'day')) return 'yesterday';

  const now = dayjs();
  if (diffDays === 6 && end.isSame(now, 'day')) return '7d';
  if (diffDays === 29 && end.isSame(now, 'day')) return '30d';
  if (diffDays === 89 && end.isSame(now, 'day')) return '90d';
  if (diffDays === 364 && end.isSame(now, 'day')) return '1y';
  if (start.isSame(now.startOf('week'), 'day') && end.isSame(now, 'day')) return 'week';
  if (start.isSame(now.startOf('month'), 'day') && end.isSame(now, 'day')) return 'month';

  return 'custom';
};

/* ----------------------------------------------------------
   ✅ Thunk: Fetch Ticket Tag Usage Stats
----------------------------------------------------------- */
export const getAgentsChatsStats = createAsyncThunk(
  'agentsChatsStats/getAgentsChatsStats',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authToken = state.loginToken?.response?.token;
      const businessId = state.loginToken?.response?.user?.business_id;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Karachi';

      if (!businessId) throw new Error('Business ID not found');

      let startDate = params.startDate;
      let endDate = params.endDate;
      let range = params.range;
      const group_id = params.group_id;

      if (!startDate || !endDate) {
        endDate = dayjs().format('YYYY-MM-DD');
        startDate = dayjs().subtract(6, 'days').format('YYYY-MM-DD');
        range = '7d';
      }

      if (!range) range = detectRangeType(startDate, endDate);

      // ✅ Build manual (non-encoded) query string
      let query = `timezone=${timezone}&range=${range}`;
      if (range === 'custom') {
        query += `&from=${startDate}&to=${endDate}`;
      }
      if (group_id) {
        query += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const url = `${ROOT_URL}/analytics/agent/chat-stats/${businessId}?${query}`;

      console.log('📊 Requesting Ticket Performance Stats From:', url);

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error fetching ticket tag usage stats:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch ticket tag usage stats'
      );
    }
  }
);

/* ----------------------------------------------------------
   ✅ Thunk: Initialize Default Tag Stats (7-Day Range)
----------------------------------------------------------- */
export const initAgentsChatsStats = createAsyncThunk(
  'agentsChatsStats/initAgentsChatsStats',
  async ({ group_id } = {}, { dispatch }) => {
    const startDate = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');
    await dispatch(getAgentsChatsStats({ startDate, endDate, range: '7d', group_id }));
  }
);

/* ----------------------------------------------------------
   ✅ Initial State
----------------------------------------------------------- */
const initialState = {
  loading: false,
  error: null,
  data: null,
  lastFetchKey: null,
  ticketData: [],
  range: '7d',
  dateRange: {
    startDate: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  },
};

/* ----------------------------------------------------------
   ✅ Slice Definition
----------------------------------------------------------- */
const agentsChatsStatsSlice = createSlice({
  name: 'agentsChatsStats',
  initialState,
  reducers: {
    resetAgentsChatsStats: () => initialState,
    setDateRange: (state, action) => {
      const { startDate, endDate } = action.payload;
      state.dateRange = { startDate, endDate };
      state.range = detectRangeType(startDate, endDate);
    },
    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getAgentsChatsStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, action.meta?.arg);
        state.error = null;
      })
      .addCase(getAgentsChatsStats.fulfilled, (state, action) => {
        state.loading = false;
        markAnalyticsFetchKey(state, action.meta?.arg);
        state.error = null;
        const payload = action.payload;

        if (payload?.data) {
          state.data = payload.data;
          state.ticketData = payload.data.data || payload.data.tags || [];
          state.range = payload.data.range || state.range;
          state.dateRange = {
            startDate: payload.data.from || state.dateRange.startDate,
            endDate: payload.data.to || state.dateRange.endDate,
          };
        } else if (payload?.tags) {
          state.data = payload;
          state.ticketData = payload.tags;
          state.range = payload.range || state.range;
          state.dateRange = {
            startDate: payload.from || state.dateRange.startDate,
            endDate: payload.to || state.dateRange.endDate,
          };
        } else if (Array.isArray(payload)) {
          state.data = { tags: payload };
          state.ticketData = payload;
        } else {
          state.data = payload;
          state.ticketData = [];
        }

        console.log('✅ Tag usage data loaded:', state.ticketData.length, 'tags');
      })
      .addCase(getAgentsChatsStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = null;
        state.ticketData = [];
        console.error('❌ Tag usage loading failed:', action.payload);
      })
      .addCase(initAgentsChatsStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, { range: '7d', group_id: action.meta?.arg?.group_id });
      })
      .addCase(initAgentsChatsStats.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(initAgentsChatsStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetAgentsChatsStats, setDateRange, clearError } =
  agentsChatsStatsSlice.actions;

export default agentsChatsStatsSlice.reducer;
